(() => {
  const root = document.documentElement;
  const storageKey = 'techno-color-mode';
  const media = window.matchMedia('(prefers-color-scheme: dark)');

  const rememberChoice = () => root.dataset.technoRememberMode === 'true';
  const defaultMode = () => root.dataset.technoModeDefault || 'system';
  const toggleEnabled = () => root.dataset.technoModeEnabled === 'true';

  const readSavedMode = () => {
    if (!rememberChoice()) return null;
    try {
      const value = localStorage.getItem(storageKey);
      return value === 'light' || value === 'dark' ? value : null;
    } catch (error) {
      return null;
    }
  };

  const saveMode = (mode) => {
    if (!rememberChoice()) return;
    try {
      localStorage.setItem(storageKey, mode);
    } catch (error) {
      // Storage can be unavailable in privacy-restricted contexts.
    }
  };

  const setDock = (open) => {
    const dock = document.querySelector('[data-techno-side-dock]');
    if (!dock) return;
    const trigger = dock.querySelector('[data-techno-side-dock-trigger]');
    const panel = dock.querySelector('[data-techno-side-dock-panel]');
    if (!trigger || !panel) return;
    trigger.setAttribute('aria-expanded', String(open));
    dock.classList.toggle('is-open', open);
    panel.hidden = !open;
    if (open) panel.querySelector('[data-techno-mode-toggle]')?.focus();
  };

  const updateControls = () => {
    const dark = root.dataset.colorMode === 'dark';
    document.querySelectorAll('[data-techno-mode-toggle]').forEach((button) => {
      const lightStateLabel = button.dataset.lightStateLabel || 'Lights off';
      const darkStateLabel = button.dataset.darkStateLabel || 'Lights on';
      const switchToDarkLabel = button.dataset.switchToDarkLabel || 'Switch to dark mode';
      const switchToLightLabel = button.dataset.switchToLightLabel || 'Switch to light mode';

      button.setAttribute('aria-pressed', String(dark));
      button.setAttribute('aria-label', dark ? switchToLightLabel : switchToDarkLabel);

      const label = button.querySelector('[data-techno-mode-label]');
      if (label) label.textContent = dark ? darkStateLabel : lightStateLabel;
    });
  };

  const updateThemeColor = (mode) => {
    const themeColor = document.querySelector('meta[name="theme-color"]');
    if (!themeColor) return;
    themeColor.content = mode === 'dark'
      ? (root.dataset.technoDarkThemeColor || '#191C1F')
      : (root.dataset.technoLightThemeColor || '#F2F4F6');
  };

  const applyMode = (mode, persist = false) => {
    const resolvedMode = mode === 'dark' ? 'dark' : 'light';
    root.dataset.colorMode = resolvedMode;
    root.style.colorScheme = resolvedMode;
    updateThemeColor(resolvedMode);
    if (persist) saveMode(resolvedMode);
    updateControls();
    document.dispatchEvent(new CustomEvent('techno:modechange', { detail: { mode: resolvedMode } }));
  };

  const resolveAutomaticMode = () => {
    const configured = defaultMode();
    if (configured === 'light' || configured === 'dark') return configured;
    return media.matches ? 'dark' : 'light';
  };

  document.addEventListener('click', (event) => {
    const dockTrigger = event.target.closest('[data-techno-side-dock-trigger]');
    if (dockTrigger) {
      setDock(dockTrigger.getAttribute('aria-expanded') !== 'true');
      return;
    }

    if (event.target.closest('[data-techno-side-dock-close]')) {
      setDock(false);
      document.querySelector('[data-techno-side-dock-trigger]')?.focus();
      return;
    }

    const toggle = event.target.closest('[data-techno-mode-toggle]');
    if (toggle && toggleEnabled()) {
      applyMode(root.dataset.colorMode === 'dark' ? 'light' : 'dark', true);
      return;
    }

    const dock = document.querySelector('[data-techno-side-dock]');
    if (dock?.classList.contains('is-open') && !event.target.closest('[data-techno-side-dock]')) setDock(false);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const dock = document.querySelector('[data-techno-side-dock]');
    if (!dock?.classList.contains('is-open')) return;
    setDock(false);
    dock.querySelector('[data-techno-side-dock-trigger]')?.focus();
  });

  const onSystemChange = (event) => {
    if (defaultMode() !== 'system' || readSavedMode()) return;
    applyMode(event.matches ? 'dark' : 'light');
  };

  if (media.addEventListener) media.addEventListener('change', onSystemChange);
  else media.addListener(onSystemChange);

  const initialize = () => {
    applyMode(readSavedMode() || resolveAutomaticMode());
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
})();
